import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../../api';
import { contentEndpoints, sponsorEndpoints } from '../../api';
import { ContentEditor } from '../../content/ContentEditor';
import { Button, Panel } from '../../ui';
import type { PanelProps } from '../Event';

/**
 * The event's page (D-88, PARTS 1, 2, 5, 8).
 *
 * What is edited here is the page a customer reads, and it is the only place
 * an event's copy lives: the old Markdown description and its fallback were
 * removed with D-103. An event with nothing here has no page copy, and the
 * storefront says "Details coming soon."
 */

export function ContentPanel({ detail, onError, can }: PanelProps) {
  const queryClient = useQueryClient();
  const eventId = detail.event.id;
  const edit = can('events.content.edit');
  const [preview, setPreview] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  const content = useQuery({ queryKey: ['event-content', eventId], queryFn: () => contentEndpoints.get(eventId) });
  const revisions = useQuery({ queryKey: ['event-content-revisions', eventId], queryFn: () => contentEndpoints.revisions(eventId) });
  const sponsors = useQuery({ queryKey: ['event-sponsors', eventId], queryFn: () => sponsorEndpoints.forEvent(eventId) });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['event-content', eventId] });
    void queryClient.invalidateQueries({ queryKey: ['event-content-revisions', eventId] });
  };
  const fail = (cause: ApiError) => onError(cause);

  const publish = useMutation({
    mutationFn: ({ doc, note: message }: { doc: unknown; note: string | null }) => contentEndpoints.publish(eventId, doc, message),
    onSuccess: (result) => {
      setNote('Published. The page now says this.');
      setPreview(result.html);
      refresh();
    },
    onError: fail,
  });

  const autosave = useMutation({
    mutationFn: (doc: unknown) => contentEndpoints.saveDraft(eventId, doc),
    onSuccess: (result) => setSaving(`Draft saved ${new Date(result.saved_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`),
    onError: fail,
  });

  const previewIt = useMutation({
    mutationFn: (doc: unknown) => contentEndpoints.preview(eventId, doc),
    onSuccess: (result) => setPreview(result.html),
    onError: fail,
  });

  const discardDraft = useMutation({ mutationFn: () => contentEndpoints.discardDraft(eventId), onSuccess: refresh, onError: fail });

  const onAutosave = useCallback((doc: unknown) => autosave.mutate(doc), [autosave]);

  if (content.isPending) return <p className="text-sm text-ink-400">Loading…</p>;
  if (!content.data) return null;

  // An unsaved draft wins on open: it is the newer work, and discarding it is a choice.
  const startingDocument = content.data.draft?.content ?? content.data.content;

  return (
    <div className="space-y-4">
      {note ? <p className="rounded border border-line bg-surface px-4 py-2 text-sm text-ink-700">{note}</p> : null}

      {content.data.draft ? (
        <p className="flex flex-wrap items-center gap-3 rounded border border-warn/40 bg-warn-soft px-4 py-2 text-sm text-ink-900">
          There is an unsaved draft from {new Date(content.data.draft.saved_at).toLocaleString('en-US')}
          {content.data.draft.staff ? ` by ${content.data.draft.staff}` : ''}. It is what you are editing.
          <Button onClick={() => discardDraft.mutate()}>Discard it and start from the published page</Button>
        </p>
      ) : null}

      <Panel title="The event page" description="What a customer reads. Headings start at level two, because the page's one heading is the event's title.">
        <div className="px-5 py-4">
          {edit ? (
            <ContentEditor
              key={content.data.draft?.saved_at ?? 'published'}
              content={startingDocument}
              sponsors={sponsors.data?.sponsors.map((s) => ({ id: s.sponsor_id, name: s.name, display_name: s.display_name, tier_code: s.tier_code, logos: s.logo_file_id ? [{ variant_code: 'COLOR', file_id: s.logo_file_id }] : [] })) ?? []}
              busy={publish.isPending}
              onAutosave={onAutosave}
              onPublish={(doc, message) => publish.mutate({ doc, note: message })}
              onPreview={(doc) => previewIt.mutate(doc)}
            />
          ) : (
            <p className="text-sm text-ink-600">You can read this page but not change it.</p>
          )}
          {saving ? <p className="mt-2 text-xs text-ink-400">{saving}</p> : null}
        </div>
      </Panel>

      {preview ? (
        <Panel title="Preview" description="Rendered by the same code the storefront uses." actions={<Button onClick={() => setPreview(null)}>Close</Button>}>
          {/* The HTML came from our own renderer, which builds it from known
              nodes with escaped text — there is no author-supplied markup in it. */}
          <div className="content-preview px-5 py-4" dangerouslySetInnerHTML={{ __html: preview }} />
        </Panel>
      ) : null}

      <Panel title="History">
        {revisions.data?.revisions.length === 0 ? (
          <p className="px-5 py-4 text-sm text-ink-600">Nothing published yet.</p>
        ) : (
          <ul className="divide-y divide-line">
            {(revisions.data?.revisions ?? []).map((revision) => (
              <li key={revision.id} className="flex flex-wrap items-center gap-3 px-5 py-2 text-sm">
                <span className="text-ink-900">{new Date(revision.created_at).toLocaleString('en-US')}</span>
                <span className="text-ink-400">{revision.kind === 'DRAFT' ? 'autosaved draft' : 'published'}</span>
                {revision.staff ? <span className="text-ink-600">{revision.staff}</span> : null}
                {revision.note ? <span className="text-ink-600">“{revision.note}”</span> : null}
                <Button
                  className="ml-auto"
                  onClick={() =>
                    void contentEndpoints
                      .revision(eventId, revision.id)
                      .then((result) => previewIt.mutate(result.revision.content))
                      .catch(fail)
                  }
                >
                  Preview this
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
